import React, { useEffect, useState } from 'react';
import type {
  WorklogReportResponse,
  WorklogReportGroup,
  WorklogReportRow,
} from '../../api/report.api';
import { getTimesheetConfig } from '../../api/timesheet-approval.api';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function formatStatus(status: string | null): string {
  if (!status) return '\u2013'; // en-dash
  switch (status) {
    case 'approved':
      return 'Closed Approved';
    case 'rejected':
      return 'Closed Rejected';
    case 'submitted':
      return 'Submitted';
    case 'reopened':
      return 'Reopened';
    default:
      return status;
  }
}

function getStatusClass(status: string | null): string {
  if (!status) return '';
  switch (status) {
    case 'approved':
      return 'rp-worklog-report-table__status--approved';
    case 'rejected':
      return 'rp-worklog-report-table__status--rejected';
    case 'submitted':
      return 'rp-worklog-report-table__status--submitted';
    case 'reopened':
      return 'rp-worklog-report-table__status--reopened';
    default:
      return '';
  }
}

/** Aggregate hours / counts / billable split for a group of worklog rows. */
function computeGroupTotals(rows: WorklogReportRow[]): {
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  count: number;
} {
  let totalHours = 0;
  let billableHours = 0;
  let nonBillableHours = 0;
  for (const row of rows) {
    totalHours += row.hours;
    if (row.categoryIsBillable === true) billableHours += row.hours;
    else nonBillableHours += row.hours;
  }
  return {
    totalHours: Math.round(totalHours * 100) / 100,
    billableHours: Math.round(billableHours * 100) / 100,
    nonBillableHours: Math.round(nonBillableHours * 100) / 100,
    count: rows.length,
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  data: WorklogReportResponse;
}

const WorklogReportTable: React.FC<Props> = ({ data }) => {
  const { groups } = data;

  // Show Status + Approved-by columns only when timesheet approval workflow is enabled.
  const [approvalEnabled, setApprovalEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    getTimesheetConfig()
      .then((cfg) => {
        if (!cancelled) setApprovalEnabled(cfg.approval_enabled);
      })
      .catch(() => {
        if (!cancelled) setApprovalEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const showApprovalColumns = approvalEnabled === true;

  return (
    <div className="rp-report-table__wrapper">
      <div className="rp-report-table__scroll">
        <table className="rp-report-table rp-worklog-report-table">
          <thead>
            <tr>
              {showApprovalColumns && (
                <>
                  <th className="rp-worklog-report-table__col-status">Status</th>
                  <th className="rp-worklog-report-table__col-approver">Approved by</th>
                </>
              )}
              <th className="rp-worklog-report-table__col-person">Person</th>
              <th className="rp-worklog-report-table__col-date">Date</th>
              <th className="rp-worklog-report-table__col-board">Board</th>
              <th className="rp-worklog-report-table__col-item">Item</th>
              <th className="rp-worklog-report-table__col-category">Category</th>
              <th className="rp-worklog-report-table__col-hours">
                Time Spent (Hours)
              </th>
              <th className="rp-worklog-report-table__col-comment">Comment</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <WorklogGroupRows
                key={group.groupId}
                group={group}
                showApprovalColumns={showApprovalColumns}
                showGroupHeader={
                  groups.length > 1 || group.groupName !== 'All Worklogs'
                }
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Report description */}
      <div className="rp-report-description">
        <div className="rp-report-description__title">How to read this report</div>
        <p className="rp-report-description__text">
          This report lists <strong>individual worklog entries</strong> (time tracking records) for the selected period.
          Each row represents a single time entry logged by a team member against a specific board and item.
        </p>
        {showApprovalColumns && (
          <>
            <div className="rp-report-description__subtitle">Status workflow</div>
            <div className="rp-report-description__legend">
              <span className="rp-report-description__swatch rp-report-description__swatch--submitted" />
              <span>Submitted &mdash; worklog has been submitted for approval</span>
            </div>
            <div className="rp-report-description__legend">
              <span className="rp-report-description__swatch rp-report-description__swatch--approved" />
              <span>Closed Approved &mdash; worklog was reviewed and approved by a manager</span>
            </div>
            <div className="rp-report-description__legend">
              <span className="rp-report-description__swatch rp-report-description__swatch--rejected" />
              <span>Closed Rejected &mdash; worklog was reviewed and rejected</span>
            </div>
            <div className="rp-report-description__legend">
              <span className="rp-report-description__swatch rp-report-description__swatch--reopened" />
              <span>Reopened &mdash; previously closed worklog was reopened for revision</span>
            </div>
          </>
        )}
        <div className="rp-report-description__subtitle">Key details</div>
        <ul className="rp-report-description__list">
          <li>
            <strong>Time Spent</strong> shows hours as logged by the team member. These are actual hours worked, not planned/scheduled hours.
          </li>
          <li>
            The <strong>Group by</strong> option organizes rows under <em>Board</em>, <em>Item</em>, or <em>Category</em> headers.
          </li>
          {showApprovalColumns && (
            <li>
              The <strong>Approved by</strong> column shows who reviewed the entry. A dash (&ndash;) means no approval action has been taken yet.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

// ─── Group rows ──────────────────────────────────────────────────────────────

const WorklogGroupRows: React.FC<{
  group: WorklogReportGroup;
  showGroupHeader: boolean;
  showApprovalColumns: boolean;
}> = ({ group, showGroupHeader, showApprovalColumns }) => {
  const colSpan = showApprovalColumns ? 9 : 7;
  const totals = showGroupHeader ? computeGroupTotals(group.rows) : null;
  return (
    <>
      {showGroupHeader && totals && (
        <tr className="rp-report-table__group-row">
          <td colSpan={colSpan}>
            <div className="rp-worklog-report-table__group-header">
              <strong className="rp-worklog-report-table__group-name">
                {group.groupName}
              </strong>
              <div className="rp-worklog-report-table__group-totals">
                <span className="rp-worklog-report-table__group-totals-item">
                  {totals.totalHours}h total
                </span>
                <span className="rp-worklog-report-table__group-totals-divider">·</span>
                <span className="rp-worklog-report-table__group-totals-item">
                  {totals.count} worklog{totals.count === 1 ? '' : 's'}
                </span>
                <span className="rp-worklog-report-table__group-totals-divider">·</span>
                <span className="rp-worklog-report-table__group-totals-item">
                  <span
                    className="rp-worklog-report-table__category-dot"
                    style={{ backgroundColor: '#00ca72' }}
                  />
                  {totals.billableHours}h billable
                </span>
                <span className="rp-worklog-report-table__group-totals-divider">·</span>
                <span className="rp-worklog-report-table__group-totals-item">
                  <span
                    className="rp-worklog-report-table__category-dot"
                    style={{ backgroundColor: '#fdab3d' }}
                  />
                  {totals.nonBillableHours}h non-billable
                </span>
              </div>
            </div>
          </td>
        </tr>
      )}
      {group.rows.map((row) => (
        <WorklogRow
          key={row.worklogId}
          row={row}
          showApprovalColumns={showApprovalColumns}
        />
      ))}
    </>
  );
};

// ─── Worklog row ─────────────────────────────────────────────────────────────

const WorklogRow: React.FC<{
  row: WorklogReportRow;
  showApprovalColumns: boolean;
}> = ({ row, showApprovalColumns }) => {
  return (
    <tr className="rp-report-table__user-row">
      {showApprovalColumns && (
        <>
          <td className="rp-worklog-report-table__col-status">
            <span className={`rp-worklog-report-table__status ${getStatusClass(row.approvalStatus)}`}>
              {formatStatus(row.approvalStatus)}
            </span>
          </td>
          <td className="rp-worklog-report-table__col-approver">
            {row.approverName ?? '\u2013'}
          </td>
        </>
      )}
      <td className="rp-worklog-report-table__col-person">{row.userName}</td>
      <td className="rp-worklog-report-table__col-date">{formatDate(row.date)}</td>
      <td className="rp-worklog-report-table__col-board">{row.boardName}</td>
      <td className="rp-worklog-report-table__col-item">{row.itemName}</td>
      <td className="rp-worklog-report-table__col-category">
        {row.categoryName ? (
          <span className="rp-worklog-report-table__category">
            <span
              className="rp-worklog-report-table__category-dot"
              style={{ backgroundColor: row.categoryIsBillable ? '#00ca72' : '#fdab3d' }}
            />
            {row.categoryName}
          </span>
        ) : '\u2013'}
      </td>
      <td className="rp-worklog-report-table__col-hours">{row.hours}</td>
      <td className="rp-worklog-report-table__col-comment">
        {row.description ?? ''}
      </td>
    </tr>
  );
};

export default WorklogReportTable;
